import { booleanAttribute } from "#ce/utils/props";
import { props as availabilityFilterProps } from "#ce/shared/filters/availability/components/props";
import { props as booleanFilterProps } from "#ce/shared/filters/boolean/components/props";
import { props as categoryFilterProps } from "#ce/shared/filters/category/components/props";
import { props as dateFilterProps } from "#ce/shared/filters/date/components/props";
import { props as distanceFilterProps } from "#ce/shared/filters/distance/components/props";
import { props as insurancePlanFilterProps } from "#ce/shared/filters/insurance-plan/components/props";
import { props as providerFilterProps } from "#ce/shared/filters/provider/components/props";
import { props as specialtyFilterProps } from "#ce/shared/filters/specialty/components/props";
import { props as visitTypeFilterProps } from "#ce/shared/filters/visit-type/components/props";

/** The props each filter declares, keyed by the `type` that selects it. */
const filterProps = Object.freeze({
  availability: availabilityFilterProps,
  boolean: booleanFilterProps,
  category: categoryFilterProps,
  date: dateFilterProps,
  distance: distanceFilterProps,
  insurance: insurancePlanFilterProps,
  provider: providerFilterProps,
  specialty: specialtyFilterProps,
  visitType: visitTypeFilterProps,
});

export const filterTypes = Object.freeze(Object.keys(filterProps));

/**
 * BOUND_FROM_STATE names the filter props the element binds from its own state instead of from a
 * same-named prop:
 *
 *   - modelValue:   the element's model, seeded by the `value` attribute
 *   - name:         the form name, which the form-associated host owns as its `name` attribute
 *                   and property
 *   - open:         live state, seeded by the `open` attribute
 *   - prependIcon:  the `prepend-icon` attribute, because the `prependIcon` property reports the
 *                   filter's resolved icon
 */
const BOUND_FROM_STATE = Object.freeze(["modelValue", "name", "open", "prependIcon"]);

/**
 * Returns the props `<sp-finder-filter type="…">` forwards to the filter of that type, as that
 * filter defines them. Empty for an unknown type.
 *
 * @param {String} type
 * @returns {Record<string, object>}
 */
export function forwardedPropsFor(type) {
  const declared = Object.entries(filterProps[type] ?? {});

  return Object.fromEntries(declared.filter(([name]) => !BOUND_FROM_STATE.includes(name)));
}

/**
 * Returns whether the filter of that type declares the prop.
 *
 * @param {String} type
 * @param {String} name
 * @returns {Boolean}
 */
export function filterDeclares(type, name) {
  return Object.hasOwn(filterProps[type] ?? {}, name);
}

function typesOf(definition) {
  return [].concat(definition?.type ?? []);
}

// A forwarded prop takes no default of its own, so an absent attribute leaves the filter's default
// standing. A boolean is off when absent in every filter, which is `booleanAttribute()`'s default.
function toForwardedProp(current, definition) {
  const types = [...new Set([...typesOf(current), ...typesOf(definition)])];

  if (types.includes(Boolean)) {
    return booleanAttribute(false, {
      type: types,
    });
  }

  return {
    type: types,
    default: undefined,
  };
}

function collectForwardedProps() {
  const collected = {};

  for (const type of filterTypes) {
    for (const [name, definition] of Object.entries(forwardedPropsFor(type))) {
      collected[name] = toForwardedProp(collected[name], definition);
    }
  }

  return collected;
}

/** Every prop any filter takes from the element, whichever `type` is mounted. */
export const forwardedProps = Object.freeze(collectForwardedProps());

/** The props the element reads itself. */
export const ownProps = Object.freeze({
  /**
   * The filter to render. `availability` narrows to bookable appointments; `boolean` is one
   * checkbox; `category` a list of options, flat or nested; `date` a day or a range of days;
   * `distance` a place and a radius around it; `insurance` an insurance plan; `provider` the
   * provider's name, type and affiliation; `specialty` a specialty or condition; `visitType` a
   * visit type.
   *
   * @type {String}
   * @values availability, boolean, category, date, distance, insurance, provider, specialty, visitType
   * @storybook-control select
   */
  type: {
    type: String,
    validator: (value) => filterTypes.includes(value),
    required: true,
  },
  /**
   * The filter's label: the chip's text or the form field's label, the wording its filter bar lists
   * it under, and the `label` property the legacy filter menus read. Per mount, so a prop. The
   * availability filter renders its own wording and takes this one for the listing alone.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * When set, the filter can't be cleared (legal requirement, see
   * `<sp-required-filters-modal>`). Guards clearing through the chip and the filter menu.
   *
   * @type {Boolean|String}
   */
  nonClearable: booleanAttribute(),
  /**
   * Suppresses the filter's leading icon in every chrome presentation. When false or omitted,
   * configured and filter-default leading icons retain their existing behavior.
   *
   * @type {Boolean|String}
   */
  noPrependIcon: booleanAttribute(),
  /**
   * Suppresses the decorative field-chrome label while retaining the filter label for its
   * activator's accessible name, filter-bar announcement, and public API. When false or omitted,
   * field chrome renders its label as usual.
   *
   * @type {Boolean|String}
   */
  noFieldLabel: booleanAttribute(),
  /**
   * Whether the thing this filter depends on is in place. A filter offering to narrow by
   * sub-specialty is worth showing only once a specialty is chosen; one narrowing by keyword only
   * once a keyword is searched. Omit it for a filter that depends on nothing, which is most of them.
   *
   * While `"unmet"` and inactive, the filter is **withheld**: it hides its closed host and reports
   * `withheld: true` to its bar. A withheld filter that holds a value shows anyway — a filter the
   * visitor cannot see is one they cannot clear.
   *
   * The doctor finder rewrites this per search, so it is the filter's own attribute rather than
   * something its bar configures.
   *
   * @type {String}
   * @values met, unmet
   */
  prerequisite: {
    type: String,
    default: undefined,
    validator: (value) => ["met", "unmet"].includes(value),
  },
  /** Text shown in the field activator when no value is selected. Defaults to `label`. */
  fieldPlaceholder: {
    type: String,
    default: undefined,
  },
  /**
   * `bar` renders a chip. `field` renders a visible label with a field control.
   * When omitted, a bar or legacy filter container selects chip chrome. Other mounts use
   * the filter's default activator. Chip chrome is resolved once at mount.
   *
   * @type {String}
   */
  chrome: {
    type: String,
    default: undefined,
  },
});

export const props = Object.freeze({
  ...forwardedProps,
  ...ownProps,
});
